/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable prefer-const */
// TODO: Fix ESLint errors and improve type safety

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '../../store/charge-points-store';
import type { ChargePointData } from '../../types/charge-point.types';
import { QuickFilters, type ChargePointFilters } from './QuickFilters';

interface ChargePointsListProps {
  chargePoints: ChargePointData[];
}

/**
 * Display a list of charge points (used for groups)
 */
export function ChargePointsList({ chargePoints }: ChargePointsListProps) {
  const { t } = useTranslation();
  const selectChargePoint = useChargePointsStore(
    (s: ChargePointsState) => s.selectChargePoint
  );
  const selectedChargePoint = useChargePointsStore(
    (s: ChargePointsState) => s.selectedChargePoint
  );
  const setParentGroup = useChargePointsStore(
    (s: ChargePointsState) => s.setParentGroup
  );

  const [filters, setFilters] = useState<ChargePointFilters>({
    status: 'all',
    type: 'all',
    search: '',
  });

  const [sortBy, setSortBy] = useState<'power' | 'price' | 'name' | 'status'>(
    'name'
  );

  // Check if we have AC and DC types
  const hasAC = chargePoints.some((cp) => cp.typeLabel === 'AC');
  const hasDC = chargePoints.some((cp) => cp.typeLabel === 'DC');

  // Filter and sort charge points based on active filters
  const filteredChargePoints = useMemo(() => {
    // Filter first
    let filtered = chargePoints.filter((cp) => {
      // Status filter
      if (
        filters.status === 'available' &&
        cp.chargePointState !== 'available'
      ) {
        return false;
      }

      // Type filter
      if (filters.type !== 'all' && cp.typeLabel !== filters.type) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = cp.name.toLowerCase().includes(searchLower);
        const idMatch = cp.id.toString().includes(searchLower);
        if (!nameMatch && !idMatch) {
          return false;
        }
      }

      return true;
    });

    // Then sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'power':
          return b.maxKw - a.maxKw; // Highest power first
        case 'price': {
          // For dynamic pricing (spot), amount is 0, so use amount_min for sorting
          const masterPricingA =
            a.pricingCollection?.pricingsPublic?.masterPricing;
          const masterPricingB =
            b.pricingCollection?.pricingsPublic?.masterPricing;

          const priceA =
            masterPricingA && (masterPricingA.amount ?? 0) > 0
              ? masterPricingA.amount
              : (masterPricingA?.amountMin ?? 999);
          const priceB =
            masterPricingB && (masterPricingB.amount ?? 0) > 0
              ? masterPricingB.amount
              : (masterPricingB?.amountMin ?? 999);

          return priceA - priceB; // Lowest price first
        }
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
        default: {
          // Available first, then busy, then others
          const statusOrder = {
            available: 0,
            busy: 1,
            reserved: 2,
            error: 3,
            disconnected: 4,
            passive: 5,
          };
          const orderA =
            statusOrder[a.chargePointState as keyof typeof statusOrder] ?? 6;
          const orderB =
            statusOrder[b.chargePointState as keyof typeof statusOrder] ?? 6;
          return orderA - orderB;
        }
      }
    });

    return filtered;
  }, [chargePoints, filters, sortBy]);

  if (chargePoints.length === 0) {
    return (
      <div className="text-center py-8 text-muted">
        {t('chargePoint.noChargePointsInGroup')}
      </div>
    );
  }

  // Only show filters if there are more than 5 charge points
  const showFilters = chargePoints.length > 5;

  return (
    <div className="space-y-3">
      {/* Compact Filter Bar - Only show for large lists */}
      {showFilters && (
        <QuickFilters
          onFilterChange={setFilters}
          hasAC={hasAC}
          hasDC={hasDC}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      )}

      {/* Charge Points List */}
      <div className="space-y-2">
        {filteredChargePoints.length === 0 ? (
          <div className="text-center py-8 text-muted">
            {t('chargePoint.noMatchingChargePoints') ||
              'No charge points match your filters'}
          </div>
        ) : (
          filteredChargePoints.map((cp) => (
            <ChargePointCard
              key={cp.id}
              chargePoint={cp}
              onSelect={selectChargePoint}
              parentGroup={selectedChargePoint}
              setParentGroup={setParentGroup}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Individual charge point card
 */
function ChargePointCard({
  chargePoint,
  onSelect,
  parentGroup,
  setParentGroup,
}: {
  chargePoint: ChargePointData;
  onSelect: (wrapper: any) => void;
  parentGroup: any;
  setParentGroup: (group: any) => void;
}) {
  const { t } = useTranslation();
  // Use public_label for pricing (handles both static and dynamic pricing correctly)
  const priceLabel = chargePoint.pricingCollection?.publicLabel;
  const isDynamicPricing =
    chargePoint.pricingCollection?.pricingsPublic?.dynamic;
  const state = chargePoint.chargePointState;

  // Check if there are additional fees (non-master pricing entries of type 'fee' or 'fixed_min')
  const hasAdditionalFees =
    chargePoint.pricingCollection?.pricingsPublic?.pricingEntries?.some(
      (entry) =>
        !entry.isMasterPrice &&
        (entry.type === 'fee' || entry.type === 'fixed_min')
    ) ?? false;

  // Get status color and label based on state
  const getStatusInfo = () => {
    switch (state) {
      case 'available':
        return { color: 'bg-green-500', label: t('common.status.available') };
      case 'busy':
        // Busy charge points carry a grey pin on the map, so keep the dot grey to match.
        return { color: 'bg-gray-500', label: t('common.status.busy') };
      case 'reserved':
        return { color: 'bg-blue-500', label: t('common.status.reserved') };
      case 'error':
        return { color: 'bg-red-500', label: t('common.status.error') };
      case 'disconnected':
        // Disconnected charge points carry a red pin on the map, so keep the dot red to match.
        return { color: 'bg-red-500', label: t('common.status.disconnected') };
      case 'passive':
      default:
        return { color: 'bg-gray-400', label: t('common.status.passive') };
    }
  };

  const statusInfo = getStatusInfo();

  const handleSelect = () => {
    // Set the parent group for back navigation
    setParentGroup(parentGroup);

    // Create a wrapper object matching the ChargePointWrapper type
    onSelect({
      id: `cp-${chargePoint.id}`,
      type: 'chargePoint',
      coordinate: chargePoint.coordinate,
      pin: chargePoint.pin,
      chargePoint: chargePoint,
    });
  };

  return (
    <button
      onClick={handleSelect}
      className="w-full p-3 border border-ink/10 rounded-2xl hover:border-ink/30 hover:bg-ink/5 transition-colors text-left cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left Side - Info */}
        <div className="min-w-0 flex-1 space-y-2">
          {/* Name (truncates so the price never overflows) and status */}
          <div className="flex items-start justify-between gap-2">
            <h5
              className="min-w-0 truncate font-medium text-sm text-ink"
              title={chargePoint.name}
            >
              {chargePoint.name}
            </h5>
            <span
              className={`flex shrink-0 items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white ${statusInfo.color}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-surface" />
              {statusInfo.label}
            </span>
          </div>

          {/* Connectors */}
          {chargePoint.connectors && chargePoint.connectors.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {chargePoint.connectors.map((connector) => (
                <span
                  key={connector.id}
                  className="flex items-center gap-1.5 text-xs px-2 py-1 bg-ink/5 text-ink/80 rounded"
                >
                  {connector.iconUrl && (
                    <img
                      src={connector.iconUrl}
                      alt={connector.name}
                      className="w-4 h-4 object-contain"
                    />
                  )}
                  {connector.name}
                </span>
              ))}
            </div>
          )}

          {/* Power */}
          <div className="flex items-center gap-3 text-xs text-muted">
            <span className="font-medium">{chargePoint.maxKwLabel}</span>
          </div>
        </div>

        {/* Right Side - Price */}
        {priceLabel && (
          <div className="shrink-0 text-right">
            <div className="flex items-center gap-1 justify-end">
              <div className="whitespace-nowrap text-base font-semibold tabular-nums text-ink">
                {priceLabel}
              </div>
              {isDynamicPricing && (
                <svg
                  className="w-4 h-4 text-monta-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label={t('chargePoint.dynamicPricing')}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              )}
              {hasAdditionalFees && (
                <svg
                  className="w-4 h-4 text-ink"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label={
                    t('chargePoint.additionalFees') || 'Additional fees'
                  }
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              )}
            </div>
          </div>
        )}
      </div>
    </button>
  );
}
